import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Plus, Car, Search, Gauge, Fuel, Settings2, Zap, Palette, CarFront, ShieldCheck, Clock, Timer, CalendarDays, MessageSquare, Activity, Camera, TrendingDown } from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import StockInsights from '../components/StockInsights';
import { STOCK_STATUS, STOCK_FILTERS, getStockStatusConfig } from '../constants/vehicleStatus';
import { formatPrice, formatMileage, formatPowerShort } from '../utils/formatters';
import { getTransmissionLabel, getConditionBadges, getFuelTypeLabel, getBodyTypeLabel } from '../constants/vehicleFields';

const SpecPill = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center rounded-full bg-dark-hover border border-dark-border px-2 py-0.5 text-[11px] text-secondary">
    {Icon && <Icon className="h-3 w-3 mr-1 text-muted" />}
    {children}
  </span>
);

const ConditionBadge = ({ icon: Icon, label, positive }) => (
  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
    positive ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'
  }`}>
    <Icon className="h-2.5 w-2.5" />
    {label}
  </span>
);

const StockAlertBadge = ({ type, days }) => {
  if (type === 'dormant') {
    return (
      <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-red-500/80 text-white backdrop-blur-sm">
        <Clock className="h-3 w-3" />
        {days}j en stock
      </span>
    );
  }
  if (type === 'slow_preparation') {
    return (
      <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-amber-500/80 text-white backdrop-blur-sm">
        <Timer className="h-3 w-3" />
        {days}j en prép.
      </span>
    );
  }
  return null;
};

/* ── Health sub-filter config ─────────────────────────────── */
const getDaysInStock = (createdAt) => {
  if (!createdAt) return 0;
  return Math.floor((new Date() - new Date(createdAt)) / (1000 * 60 * 60 * 24));
};

const HEALTH_FILTERS = [
  { key: 'all', label: 'Tous', dotColor: null },
  { key: 'healthy', label: 'En forme', dotColor: 'bg-green-400' },
  { key: 'toWatch', label: 'À surveiller', dotColor: 'bg-amber-400' },
  { key: 'dormant', label: 'Dormant', dotColor: 'bg-red-400' },
  { key: 'noPhotos', label: 'Sans photos', dotColor: 'bg-purple-400' },
  { key: 'overpriced', label: 'Au-dessus marché', dotColor: 'bg-orange-400' },
];

const getHealthCategory = (vehicle) => {
  const days = getDaysInStock(vehicle.created_at);
  const hasPhotos = vehicle.images?.length > 0;

  if (days > 45) return 'dormant';
  if (days >= 21 || !hasPhotos) return 'toWatch';
  return 'healthy';
};

const HEALTH_BADGE_CONFIG = {
  healthy: { label: 'En forme', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20', dotColor: 'bg-green-400' },
  toWatch: { label: 'À surveiller', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', dotColor: 'bg-amber-400' },
  dormant: { label: 'Dormant', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', dotColor: 'bg-red-400' },
};

const Stock = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(STOCK_STATUS.AVAILABLE);
  const [healthFilter, setHealthFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [alertMap, setAlertMap] = useState({});
  const [activityMap, setActivityMap] = useState({});

  // Read ?health= query param on mount and location changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const health = params.get('health');
    if (health && HEALTH_FILTERS.some(f => f.key === health)) {
      setFilter(STOCK_STATUS.AVAILABLE);
      setHealthFilter(health);
    }
  }, [location.search]);

  const handleFilterChange = (key) => {
    setFilter(key);
    if (key !== STOCK_STATUS.AVAILABLE) {
      setHealthFilter('all');
    }
    // Clear query param when user manually changes filter
    if (location.search) {
      navigate('/stock', { replace: true });
    }
  };

  const handleHealthFilterChange = (key) => {
    setHealthFilter(key);
    if (location.search) {
      navigate('/stock', { replace: true });
    }
  };

  const loadData = useCallback(async () => {
    try {
      const [vehiclesRes, settingsRes, insightsRes] = await Promise.all([
        api.get('/vehicles'),
        api.get('/settings').catch(() => ({ data: {} })),
        api.get('/vehicles/insights').catch(() => ({ data: { alerts: [], vehicle_activity: {} } })),
      ]);
      setVehicles(vehiclesRes.data.vehicles || []);
      setSettings(settingsRes.data || {});

      const insightsData = insightsRes.data || {};

      // Build alert map for badge display on cards
      const alerts = insightsData.alerts || [];
      const map = {};
      for (const alert of alerts) {
        if (alert.type === 'dormant' || alert.type === 'slow_preparation') {
          if (!map[alert.vehicle_id]) {
            map[alert.vehicle_id] = alert;
          }
        }
      }
      setAlertMap(map);

      // Vehicle activity (appointments & offers)
      setActivityMap(insightsData.vehicle_activity || {});
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Health counts (only for available vehicles)
  const healthCounts = useMemo(() => {
    const available = vehicles.filter(v =>
      v.stock_status === STOCK_STATUS.AVAILABLE || !v.stock_status
    );
    const noPhotos = available.filter(v => !v.images || v.images.length === 0);
    const overpriced = available.filter(v => {
      if (!v.market_price || !v.price) return false;
      return ((v.price - v.market_price) / v.market_price) * 100 > 5;
    });
    return {
      all: available.length,
      healthy: available.filter(v => getHealthCategory(v) === 'healthy').length,
      toWatch: available.filter(v => getHealthCategory(v) === 'toWatch').length,
      dormant: available.filter(v => getHealthCategory(v) === 'dormant').length,
      noPhotos: noPhotos.length,
      overpriced: overpriced.length,
    };
  }, [vehicles]);

  const counts = useMemo(() => {
    const base = vehicles.filter(v => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (v.make || '').toLowerCase().includes(q) || (v.model || '').toLowerCase().includes(q);
    });
    return {
      all: base.length,
      [STOCK_STATUS.AVAILABLE]: base.filter(v => v.stock_status === STOCK_STATUS.AVAILABLE || !v.stock_status).length,
      [STOCK_STATUS.RESERVED]: base.filter(v => v.stock_status === STOCK_STATUS.RESERVED).length,
      [STOCK_STATUS.IN_PREPARATION]: base.filter(v => v.stock_status === STOCK_STATUS.IN_PREPARATION).length,
      [STOCK_STATUS.SOLD]: base.filter(v => v.stock_status === STOCK_STATUS.SOLD).length,
      activity: base.filter(v => {
        const act = activityMap[v.id];
        return act && (act.appointments > 0 || act.offers > 0);
      }).length,
    };
  }, [vehicles, activityMap, search]);

  const filtered = useMemo(() => {
    const result = vehicles
      .filter(v => {
        if (filter === 'activity') {
          const act = activityMap[v.id];
          return act && (act.appointments > 0 || act.offers > 0);
        }
        if (filter === 'all') return true;
        return (v.stock_status || STOCK_STATUS.AVAILABLE) === filter;
      })
      // Apply health sub-filter (only when viewing available)
      .filter(v => {
        if (filter !== STOCK_STATUS.AVAILABLE || healthFilter === 'all') return true;
        if (healthFilter === 'noPhotos') return !v.images || v.images.length === 0;
        if (healthFilter === 'overpriced') {
          if (!v.market_price || !v.price) return false;
          return ((v.price - v.market_price) / v.market_price) * 100 > 5;
        }
        return getHealthCategory(v) === healthFilter;
      })
      .filter(v => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (v.make || '').toLowerCase().includes(q) || (v.model || '').toLowerCase().includes(q);
      });

    // Smart sort: activity first, then alerts, then rest
    result.sort((a, b) => {
      const aActivity = activityMap[a.id];
      const bActivity = activityMap[b.id];
      const aHasActivity = aActivity && (aActivity.appointments > 0 || aActivity.offers > 0);
      const bHasActivity = bActivity && (bActivity.appointments > 0 || bActivity.offers > 0);
      if (aHasActivity !== bHasActivity) return aHasActivity ? -1 : 1;

      const aHasAlert = !!alertMap[a.id];
      const bHasAlert = !!alertMap[b.id];
      if (aHasAlert !== bHasAlert) return aHasAlert ? -1 : 1;

      return 0;
    });

    return result;
  }, [vehicles, filter, healthFilter, search, activityMap, alertMap]);

  if (loading) {
    return <div className="py-12"><LoadingSpinner size="lg" /></div>;
  }

  const showHealthFilter = filter === STOCK_STATUS.AVAILABLE && healthCounts.all > 0;

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-primary">Stock véhicules</h1>
        <Link
          to="/vehicles/add"
          className="inline-flex items-center px-4 py-2 bg-carmin text-white rounded-lg hover:bg-carmin-light text-sm"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Ajouter
        </Link>
      </div>

      {/* Stock Insights Panel */}
      <StockInsights
        vehicles={vehicles}
        settings={settings}
        onSettingsChange={setSettings}
      />

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted" />
        <input
          type="text"
          placeholder="Rechercher par marque ou modèle..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-dark-input border border-dark-border rounded-lg text-sm text-primary placeholder-muted focus:outline-none focus:ring-2 focus:ring-carmin"
        />
      </div>

      {/* Status Filters */}
      <div className="flex flex-wrap gap-2 mb-3">
        {STOCK_FILTERS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => handleFilterChange(key)}
            className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              filter === key
                ? 'bg-theme-inverted text-theme-inverted'
                : 'bg-dark-hover text-secondary border border-dark-border hover:bg-dark-card'
            }`}
          >
            <Icon className="h-3.5 w-3.5 mr-1.5" />
            {label} ({counts[key]})
          </button>
        ))}
        {counts.activity > 0 && (
          <button
            onClick={() => handleFilterChange('activity')}
            className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              filter === 'activity'
                ? 'bg-indigo-500 text-white'
                : 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 hover:bg-indigo-500/20'
            }`}
          >
            <Activity className="h-3.5 w-3.5 mr-1.5" />
            Avec activité ({counts.activity})
          </button>
        )}
      </div>

      {/* Health Sub-Filters (only when "Disponibles" is active) */}
      {showHealthFilter && (
        <div className="flex flex-wrap gap-1.5 mb-6">
          {HEALTH_FILTERS.map(({ key, label, dotColor }) => {
            const count = healthCounts[key];
            if (key !== 'all' && count === 0) return null;
            const isActive = healthFilter === key;
            return (
              <button
                key={key}
                onClick={() => handleHealthFilterChange(key)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
                  isActive
                    ? 'bg-dark-card text-primary ring-1 ring-gray-500/50'
                    : 'text-muted hover:text-secondary hover:bg-dark-hover'
                }`}
              >
                {dotColor && <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotColor}`} />}
                {label}
                {key !== 'all' && <span className="text-muted">({count})</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Spacer when no health filter shown */}
      {!showHealthFilter && <div className="mb-3" />}

      {/* Vehicle list */}
      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <Car className="h-16 w-16 text-muted mx-auto mb-4" />
          <h3 className="text-lg font-medium text-primary mb-2">
            {vehicles.length === 0 ? 'Aucun véhicule' : 'Aucun résultat'}
          </h3>
          {vehicles.length === 0 ? (
            <Link to="/vehicles/add" className="text-carmin hover:text-carmin-light text-sm">
              Ajouter votre premier véhicule
            </Link>
          ) : (
            <button onClick={() => { handleFilterChange('all'); setSearch(''); }} className="text-carmin hover:text-carmin-light text-sm">
              Réinitialiser les filtres
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(vehicle => {
            const alert = alertMap[vehicle.id];
            const isAvailable = vehicle.stock_status === STOCK_STATUS.AVAILABLE || !vehicle.stock_status;
            const health = isAvailable ? getHealthCategory(vehicle) : null;
            const healthBadge = health ? HEALTH_BADGE_CONFIG[health] : null;

            return (
              <Link
                key={vehicle.id}
                to={`/vehicles/${vehicle.id}`}
                className={`bg-dark-card rounded-xl border overflow-hidden hover:border-gray-600 transition-colors ${
                  alert?.severity === 'high' ? 'border-red-500/30' :
                  alert?.severity === 'medium' ? 'border-amber-500/30' :
                  'border-dark-border'
                }`}
              >
                <div className="aspect-[4/3] bg-dark-hover relative">
                  {vehicle.images?.[0] ? (
                    <img src={vehicle.images[0]} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted">
                      <Car className="h-12 w-12" />
                    </div>
                  )}
                  {/* Stock status badge (top-right) */}
                  {vehicle.stock_status && vehicle.stock_status !== STOCK_STATUS.AVAILABLE && (
                    <span className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${getStockStatusConfig(vehicle.stock_status).badge}`}>
                      {getStockStatusConfig(vehicle.stock_status).label}
                    </span>
                  )}
                  {/* Health badge (top-right, for available vehicles) */}
                  {healthBadge && !alert && (
                    <span className={`absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium ${healthBadge.bg} ${healthBadge.color} border ${healthBadge.border} backdrop-blur-sm`}>
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${healthBadge.dotColor}`} />
                      {healthBadge.label}
                    </span>
                  )}
                  {/* Alert badge (top-left) */}
                  {alert && (
                    <StockAlertBadge type={alert.type} days={alert.days} />
                  )}
                  {/* No photos indicator (bottom-left) */}
                  {isAvailable && (!vehicle.images || vehicle.images.length === 0) && (
                    <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-purple-500/80 text-white backdrop-blur-sm">
                      <Camera className="h-3 w-3" />
                      Sans photos
                    </span>
                  )}
                </div>
                <div className="p-3.5">
                  <p className="font-semibold text-primary truncate">{vehicle.make} {vehicle.model}</p>
                  {vehicle.version && (
                    <p className="text-xs text-muted truncate mt-0.5">{vehicle.version}</p>
                  )}

                  <p className="text-lg font-bold text-carmin mt-1.5">
                    {formatPrice(vehicle.price)}
                  </p>

                  {/* Overpriced indicator */}
                  {isAvailable && vehicle.market_price && vehicle.price && ((vehicle.price - vehicle.market_price) / vehicle.market_price) * 100 > 5 && (
                    <p className="flex items-center gap-1 text-[11px] text-orange-400 mt-0.5">
                      <TrendingDown className="h-3 w-3" />
                      {Math.round(((vehicle.price - vehicle.market_price) / vehicle.market_price) * 100)}% au-dessus du marché
                    </p>
                  )}

                  {/* Specs pills */}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {vehicle.year && <SpecPill icon={null}>{vehicle.year}</SpecPill>}
                    {vehicle.mileage != null && <SpecPill icon={Gauge}>{formatMileage(vehicle.mileage)}</SpecPill>}
                    {vehicle.fuel_type && <SpecPill icon={Fuel}>{getFuelTypeLabel(vehicle.fuel_type)}</SpecPill>}
                    {vehicle.transmission && (
                      <SpecPill icon={Settings2}>{getTransmissionLabel(vehicle.transmission)}</SpecPill>
                    )}
                    {(vehicle.power_kw || vehicle.power_hp) && (
                      <SpecPill icon={Zap}>
                        {formatPowerShort(vehicle.power_kw, vehicle.power_hp)}
                      </SpecPill>
                    )}
                  </div>

                  {/* Extra details row */}
                  {(vehicle.color || vehicle.body_type || vehicle.doors) && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {vehicle.body_type && <SpecPill icon={CarFront}>{getBodyTypeLabel(vehicle.body_type)}</SpecPill>}
                      {vehicle.color && <SpecPill icon={Palette}>{vehicle.color}</SpecPill>}
                      {vehicle.doors && <SpecPill>{vehicle.doors} portes</SpecPill>}
                    </div>
                  )}

                  {/* Condition badges */}
                  {(vehicle.had_accident === false || vehicle.has_full_service_history || vehicle.warranty || vehicle.non_smoking) && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {getConditionBadges(vehicle).map(badge => (
                        <ConditionBadge key={badge.label} icon={badge.icon} label={badge.label} positive={badge.positive} />
                      ))}
                      {vehicle.warranty && <ConditionBadge icon={ShieldCheck} label={vehicle.warranty} positive />}
                    </div>
                  )}

                  {/* Activity badges (appointments & offers) */}
                  {(activityMap[vehicle.id]?.appointments > 0 || activityMap[vehicle.id]?.offers > 0) && (
                    <div className="flex items-center gap-2 mt-2">
                      {activityMap[vehicle.id]?.appointments > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-500/15 text-indigo-400 border border-indigo-500/20">
                          <CalendarDays className="h-2.5 w-2.5" />
                          {activityMap[vehicle.id].appointments} visite{activityMap[vehicle.id].appointments > 1 ? 's' : ''}
                        </span>
                      )}
                      {activityMap[vehicle.id]?.offers > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-400 border border-amber-500/20">
                          <MessageSquare className="h-2.5 w-2.5" />
                          {activityMap[vehicle.id].offers} offre{activityMap[vehicle.id].offers > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Status indicators */}
                  <div className="flex items-center gap-2 mt-2 text-xs">
                    {vehicle.images?.length > 0 && (
                      <span className="text-green-400">{'\u2713'} {vehicle.images.length} photos</span>
                    )}
                    {vehicle.generated_content && (
                      <span className="text-green-400">{'\u2713'} Contenu</span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Stock;
